import { CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatOrderStatus } from '~/utils';
import {
  statusBadgeClasses,
  type StatusBadgeSize,
} from '~/components/ui/statusBadgeClasses';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store.ts';

function getStatusBadgeClassname(status: string, size: StatusBadgeSize) {
  switch (status) {
    case 'PENDING_SELLER_CONFIRMATION':
      return statusBadgeClasses('pending', size);
    case 'CONFIRMED':
      return statusBadgeClasses('info', size);
    case 'SHIPPED':
      return statusBadgeClasses('shipped', size);
    case 'DELIVERED':
      return statusBadgeClasses('accepted', size);
    case 'CANCELLED':
      return statusBadgeClasses('declined', size);
    case 'BLOCKED':
      return statusBadgeClasses('warning', size);
    default:
      return statusBadgeClasses('neutral', size);
  }
}

export function OrderStatusBadge({
  status,
  size = 'caption',
  cancellationNote,
}: {
  status: string;
  size?: StatusBadgeSize;
  cancellationNote?: string | null;
}) {
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const { t } = useTranslation('order');

  return (
    <div className="flex flex-col gap-3">
      <span className={getStatusBadgeClassname(status, size)}>
        {formatOrderStatus(status, role)}
      </span>
      {status === 'CANCELLED' && cancellationNote && (
        <div className="p-4 rounded-lg border bg-neut-100 border-neut-200">
          <div className="flex items-start gap-2">
            <CircleAlert
              className="text-neut-400 mt-0.5 shrink-0"
              size={18}
              aria-hidden="true"
            />
            <div>
              <p className="text-sm font-medium text-neut-prim mb-1">
                {t('order.cancellation_reason')}
              </p>
              <p className="text-xs text-neut-sec">{cancellationNote}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
